import { useState, useEffect, useMemo, useReducer } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { insertInvoiceSchema } from "@/lib/shared-schema";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle } from "lucide-react";

import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  formatCurrency,
  formatHours,
  generateInvoiceNumber,
} from "@/lib/format-utils";
import {
  formatDate,
  parseLocalDate,
  fromStorageDate,
  startOfDay,
  endOfDay,
  toStorageDate,
  addDays,
  formatDateNoTZ,
} from "@/lib/date-utils";
import { formatDateForDisplay } from "@/lib/date-utils";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import { Card, CardContent } from "@/components/ui/card";
import { useLocation, useRouter } from "wouter";
import axios from "axios";

interface Engagement {
  id: number;
  clientName: string;
  clientId: number;
  projectName: string;
  hourlyRate: string;
  projectAmount?: string | number | null;
  project_amount?: string | number | null;
  startDate: string;
  endDate: string;
  status: string;
  userId: number;
  engagementType?: 'hourly' | 'project';
  engagement_type?: 'hourly' | 'project';
  client_id?: number;
  netTerms?: number;
}

// Update the form schema to handle conditional validation
const formSchema = z.object({
  clientName: z.string().min(1, "Client name is required"),
  engagementId: z
    .string()
    .or(z.number())
    .refine((val) => Number(val) > 0, {
      message: "Engagement is required",
    }),
  periodStart: z.string().optional(),
  periodEnd: z.string().optional(),
  notes: z.string().optional().default(""),
  invoiceAmount: z.coerce.number().nonnegative("Amount must be 0 or greater").optional().default(0),
});

type FormValues = z.infer<typeof formSchema>;

interface InvoiceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  periodStart?: Date | null;
  periodEnd?: Date | null;
  engagementId?: string | null;
  invoice?: any; // The invoice to edit (if in edit mode)
}

interface TimeLog {
  id: number;
  userId: number;
  engagementId: number;
  date: string;
  hours: number;
  description: string | null;
  hourlyRate?: string;
  billableAmount?: number;
  projectName?: string;
  clientName?: string;
  engagement?: {
    id: number;
    clientName: string;
    projectName: string;
    hourlyRate: string;
  };
}

interface LineItem {
  description: string;
  hours: number;
  rate: number;
  amount: number;
}

interface Summary {
  hours: number;
  amount: number;
}

// Update the InvoiceModalState type
type InvoiceModalState = {
  isLoading: boolean;
  isEditMode: boolean;
  selectedClientName: string;
  invoice_type: 'hourly' | 'project' | null;
  timeLogs: TimeLog[];
  invoiceTotal: number;
  totalHours: number;
  invoiceAmount: number;
  exceedsProjectAmount: boolean;
  error: string | null;
  isLoadingTimeLogs: boolean;
};

type InvoiceModalAction =
  | { type: 'INIT_EDIT_MODE'; payload: any }
  | { type: 'SET_CLIENT'; payload: string }
  | { type: 'SET_ENGAGEMENT'; payload: { engagement: Engagement; type: 'hourly' | 'project'; amount?: number } }
  | { type: 'SET_TIME_LOGS'; payload: { logs: TimeLog[]; total: number; hours: number } }
  | { type: 'SET_INVOICE_AMOUNT'; payload: number }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_LOADING_TIME_LOGS'; payload: boolean }
  | { type: 'RESET' };

// Update the reducer to handle type safety
function invoiceModalReducer(state: InvoiceModalState, action: InvoiceModalAction): InvoiceModalState {
  switch (action.type) {
    case 'INIT_EDIT_MODE': {
      const invoice = action.payload;
      return {
        ...state,
        isEditMode: true,
        selectedClientName: invoice.clientName || invoice.client_name || "",
        invoice_type: invoice.invoiceType || invoice.invoice_type || 'hourly',
        invoiceTotal: invoice.totalAmount || invoice.total_amount || 0,
        totalHours: invoice.totalHours || invoice.total_hours || 0,
        invoiceAmount: invoice.totalAmount || invoice.total_amount || 0,
        isLoading: false,
        isLoadingTimeLogs: false
      };
    }

    case 'SET_CLIENT':
      return {
        ...state,
        selectedClientName: action.payload,
        invoice_type: null,
        timeLogs: [],
        invoiceTotal: 0,
        totalHours: 0,
        invoiceAmount: 0,
        exceedsProjectAmount: false,
        isLoadingTimeLogs: false
      };

    case 'SET_ENGAGEMENT':
      console.log("SET_ENGAGEMENT reducer called with:", action.payload);
      const { engagement, type } = action.payload;
      const projectAmount = type === 'project'
        ? Number(engagement.projectAmount || engagement.project_amount || 0)
        : 0;

      return {
        ...state,
        invoice_type: type,
        invoiceAmount: projectAmount,
        invoiceTotal: projectAmount,
        timeLogs: type === 'project' ? [] : state.timeLogs,
        isLoadingTimeLogs: false
      };

    case 'SET_TIME_LOGS':
      return {
        ...state,
        timeLogs: action.payload.logs,
        invoiceTotal: action.payload.total,
        totalHours: action.payload.hours,
        isLoading: false,
        isLoadingTimeLogs: false
      };

    case 'SET_INVOICE_AMOUNT':
      return {
        ...state,
        invoiceAmount: action.payload,
        invoiceTotal: action.payload
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isLoading: false
      };

    case 'SET_LOADING_TIME_LOGS':
      return {
        ...state,
        isLoadingTimeLogs: action.payload
      };

    case 'RESET':
      return {
        isLoading: false,
        isEditMode: false,
        selectedClientName: "",
        invoice_type: null,
        timeLogs: [],
        invoiceTotal: 0,
        totalHours: 0,
        invoiceAmount: 0,
        exceedsProjectAmount: false,
        error: null,
        isLoadingTimeLogs: false
      };

    default:
      return state;
  }
}

// Update the initial state
const initialState: InvoiceModalState = {
  isLoading: false,
  isEditMode: false,
  selectedClientName: "",
  invoice_type: null,
  timeLogs: [],
  invoiceTotal: 0,
  totalHours: 0,
  invoiceAmount: 0,
  exceedsProjectAmount: false,
  error: null,
  isLoadingTimeLogs: false
};

// Add this interface after the existing interfaces
interface InvoiceSubmission {
  engagementId: number;
  userId: number;
  clientName: string;
  projectName: string;
  invoiceNumber: string;
  issueDate: string;
  dueDate: string;
  totalAmount: number;
  totalHours: number;
  lineItems: LineItem[];
  status: string;
  periodStart: string;
  periodEnd: string;
  notes: string;
  invoice_type: 'hourly' | 'project';
  billingContactName?: string;
  billingContactEmail?: string;
  billingAddress?: string;
  billingCity?: string;
  billingState?: string;
  billingZip?: string;
  billingCountry?: string;
}

const InvoiceModal = ({
  open,
  onOpenChange,
  onSuccess,
  periodStart,
  periodEnd,
  engagementId,
  invoice,
}: InvoiceModalProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Replace multiple useState calls with useReducer
  const [state, dispatch] = useReducer(invoiceModalReducer, initialState);

  console.log("🔍 InvoiceModal render - state:", state);
  console.log("🔍 InvoiceModal render - props:", { open, periodStart, periodEnd, engagementId });

  // Fetch all active engagements using the direct endpoint
  const { data: rawEngagements = [], isLoading: isLoadingEngagements } = useQuery<
    Engagement[]
  >({
    queryKey: ["/api/engagements"],
    enabled: open,
  });

  // Normalize engagement data to ensure it has consistent properties
  const normalizeEngagement = (engagement: Engagement) => {
    const normalized = { ...engagement };

    // Handle different naming conventions between API responses
    if ('project_name' in engagement) {
      normalized.projectName = engagement.project_name as string;
    }
    if ('engagement_type' in engagement) {
      normalized.engagementType = engagement.engagement_type as 'hourly' | 'project';
    }
    if ('hourly_rate' in engagement) {
      normalized.hourlyRate = engagement.hourly_rate as string;
    }

    return normalized;
  };

  // Apply normalization to all engagements
  const engagements = useMemo(() => {
    const normalized = rawEngagements.map(normalizeEngagement);
    return normalized;
  }, [rawEngagements]);

  // Get unique clients from active engagements
  const uniqueClients = useMemo(() => {
    const clients = engagements
      .filter((engagement: Engagement) =>
        // Only include engagements that have a clientName
        engagement.clientName && typeof engagement.clientName === 'string' &&
        engagement.clientName.trim() !== ''
      )
      .map((engagement: Engagement) => engagement.clientName)
      .filter((name, index, array) => array.indexOf(name) === index)
      .sort();

    return clients;
  }, [engagements]);

  // Filter active engagements by selected client
  const filteredEngagements = useMemo(() => {
    const filtered = engagements.filter(
      (engagement: Engagement) =>
        engagement.clientName === state.selectedClientName &&
        engagement.status === "active"
    );

    return filtered;
  }, [engagements, state.selectedClientName]);

  // Get the next invoice number (in a real app this would come from the server)
  const nextInvoiceNumber = generateInvoiceNumber("INV", 25);

  // Set up form with validation
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      clientName: "",
      engagementId: "",
      periodStart: "",
      periodEnd: "",
      notes: "",
      invoiceAmount: 0,
    },
  });

  const { handleSubmit, watch, setValue, control, formState: { errors, isValid } } = form;

  // Watch form values for debugging
  const watchClientName = watch("clientName");
  const watchEngagementId = watch("engagementId");
  const watchPeriodStart = watch("periodStart");
  const watchPeriodEnd = watch("periodEnd");
  const watchInvoiceAmount = watch("invoiceAmount");

  console.log("🔍 Form watch values:", {
    clientName: watchClientName,
    engagementId: watchEngagementId,
    periodStart: watchPeriodStart,
    periodEnd: watchPeriodEnd,
    invoiceAmount: watchInvoiceAmount,
    isValid,
    errors
  });

  // Update selectedClientName when client changes in form
  useEffect(() => {
    if (watchClientName) {
      dispatch({ type: 'SET_CLIENT', payload: watchClientName });
    }
  }, [watchClientName]);

  // Reset engagement if client changes
  useEffect(() => {
    // Reset engagement when client changes
    if (state.selectedClientName && watchEngagementId) {
      // Check if the selected engagement is valid for this client
      const validEngagement = filteredEngagements.some(
        (e: any) => e.id.toString() === watchEngagementId.toString(),
      );

      if (!validEngagement) {
        // Reset engagement if it doesn't belong to the selected client
        form.setValue("engagementId", "");
      }
    }
  }, [state.selectedClientName, watchEngagementId, filteredEngagements, form]);

  // Update the useEffect for engagement type changes
  useEffect(() => {
    if (watchEngagementId) {
      const selectedEngagement = filteredEngagements.find(
        (e) => e.id.toString() === watchEngagementId.toString()
      );

      if (selectedEngagement) {
        const engagementType = selectedEngagement.engagementType || selectedEngagement.engagement_type;

        if (!engagementType || !['hourly', 'project'].includes(engagementType)) {
          console.error("Invalid engagement type:", engagementType);
          return;
        }

        console.log("Setting engagement type:", engagementType, "for engagement:", selectedEngagement.id);

        dispatch({
          type: 'SET_ENGAGEMENT',
          payload: {
            engagement: selectedEngagement,
            type: engagementType,
            amount: engagementType === 'project' ? Number(selectedEngagement.projectAmount || selectedEngagement.project_amount || 0) : undefined
          }
        });
      }
    }
  }, [watchEngagementId, filteredEngagements]);

  // Check if invoice amount exceeds project amount
  useEffect(() => {
    if (state.invoice_type === 'project' && watchEngagementId) {
      const selectedEngagement = filteredEngagements.find(
        (e) => e.id.toString() === watchEngagementId.toString()
      );

      if (selectedEngagement && selectedEngagement.projectAmount) {
        const projectAmount = typeof selectedEngagement.projectAmount === 'string'
          ? parseFloat(selectedEngagement.projectAmount)
          : Number(selectedEngagement.projectAmount);

        // Ensure watchInvoiceAmount is treated as a number
        const currentInvoiceAmount = typeof watchInvoiceAmount === 'number' ? watchInvoiceAmount : 0;

        dispatch({
          type: 'SET_INVOICE_AMOUNT',
          payload: currentInvoiceAmount > projectAmount ? projectAmount : currentInvoiceAmount
        });
      }
    }
  }, [watchInvoiceAmount, watchEngagementId, filteredEngagements, state.invoice_type]);

  // Simplify the fetchTimeLogs function to be more direct
  const fetchTimeLogs = async (engagementId: string | number, startDate: string, endDate: string) => {
    if (!engagementId || !startDate || !endDate) {
      console.log("Missing required parameters for time log fetch");
      return;
    }

    try {
      dispatch({ type: 'SET_LOADING_TIME_LOGS', payload: true });

      // Convert dates to start/end of day and format as ISO strings
      const parsedStart = parseLocalDate(startDate);
      const parsedEnd = parseLocalDate(endDate);

      if (!parsedStart || !parsedEnd) {
        console.error("Invalid date format");
        return;
      }

      const start = startOfDay(parsedStart);
      const end = endOfDay(parsedEnd);

      console.log("Fetching time logs with params:", {
        engagementId: String(engagementId),
        startDate: toStorageDate(start),
        endDate: toStorageDate(end)
      });

      // Fetch all time logs for the user
      const response = await apiRequest('GET', '/api/time-logs');
      const allTimeLogs = await response.json() as TimeLog[];

      // Filter time logs by engagement and date range on the frontend
      const filteredTimeLogs = allTimeLogs.filter(log => {
        const logDate = new Date(log.date);
        const logEngagementId = log.engagementId.toString();
        const targetEngagementId = engagementId.toString();

        return (
          logEngagementId === targetEngagementId &&
          logDate >= start &&
          logDate <= end
        );
      });

      // Add billableAmount calculation to each time log
      const timeLogsWithBillableAmount = filteredTimeLogs.map(log => ({
        ...log,
        billableAmount: log.hours * parseFloat(log.hourlyRate || '0'),
        engagement: {
          id: log.engagementId,
          clientName: log.clientName || '',
          projectName: log.projectName || '',
          hourlyRate: log.hourlyRate || '0'
        }
      }));

      // Calculate totals
      const total = timeLogsWithBillableAmount.reduce((sum, log) => sum + log.billableAmount, 0);
      const hours = timeLogsWithBillableAmount.reduce((sum, log) => sum + log.hours, 0);

      console.log(`Found ${timeLogsWithBillableAmount.length} time logs for engagement ${engagementId} in date range`);
      console.log(`Total hours: ${hours}, Total amount: ${total}`);

      dispatch({
        type: 'SET_TIME_LOGS',
        payload: {
          logs: timeLogsWithBillableAmount,
          total,
          hours
        }
      });

      return timeLogsWithBillableAmount;
    } catch (error) {
      console.error("Error fetching time logs:", error);
      dispatch({
        type: 'SET_ERROR',
        payload: "Failed to load time logs for this period."
      });
      return [];
    } finally {
      dispatch({ type: 'SET_LOADING_TIME_LOGS', payload: false });
    }
  };

  // Simplify the period change effect
  useEffect(() => {
    // Skip if we're missing required data or if it's a project-based engagement
    if (!watchEngagementId || !watchPeriodStart || !watchPeriodEnd || state.invoice_type === 'project') {
      return;
    }

    // Skip fetching time logs for project-based engagements
    const selectedEngagement = filteredEngagements.find(
      (e) => e.id.toString() === watchEngagementId.toString()
    );

    if (!selectedEngagement) return;

    const engagementType = selectedEngagement.engagementType || selectedEngagement.engagement_type;
    if (engagementType === 'project') {
      dispatch({
        type: 'SET_TIME_LOGS',
        payload: {
          logs: [],
          total: 0,
          hours: 0
        }
      });
      return;
    }

    // Fetch time logs using the raw form values
    fetchTimeLogs(watchEngagementId, watchPeriodStart, watchPeriodEnd);
  }, [watchEngagementId, watchPeriodStart, watchPeriodEnd]);

  // Format hours for display
  const formatHours = (hours: number) => {
    return hours.toFixed(1);
  };

  // Define close handler
  const handleClose = () => {
    console.log("handleClose called");

    // Only reset the form and close if we're not in the middle of a submission
    if (!isSubmitting) {
      // Reset form first to avoid state validation errors
      form.reset({
        clientName: "",
        engagementId: "",
        periodStart: "",
        periodEnd: "",
        notes: "",
        invoiceAmount: 0
      });

      // Clear out all state completely
      dispatch({ type: 'RESET' });

      // Finally close the modal with a callback for success
      if (onSuccess) {
        onSuccess();
      }

      // Close the modal last
      onOpenChange(false);
    } else {
      console.log("handleClose ignored because form is submitting");
    }
  };

  // This is a dependency-free effect that will only run once when the component mounts
  useEffect(() => {
    // Initialization logic for the modal
    console.log("Invoice modal initialized");

    // Cleanup function
    return () => {
      console.log("Invoice modal cleanup");
    };
  }, []); // Empty dependency array means this only runs once

  // Reset form when modal opens/closes
  useEffect(() => {
    function extractDatePart(dateStr: string | undefined | null): string {
      if (!dateStr) return "";
      // If already YYYY-MM-DD, return as is
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
      // If ISO string, extract date part
      if (typeof dateStr === "string" && dateStr.includes("T")) return dateStr.split("T")[0];
      // Fallback: try to parse and format
      const d = new Date(dateStr);
      if (!isNaN(d.getTime())) return toStorageDate(d);
      return "";
    }

    if (open && invoice) {
      console.log("Setting up edit mode with invoice:", invoice);

      dispatch({ type: 'INIT_EDIT_MODE', payload: invoice });

      // Handle both camelCase and snake_case property names
      const periodStartValue = invoice.periodStart || invoice.period_start;
      const periodEndValue = invoice.periodEnd || invoice.period_end;
      const clientNameValue = invoice.clientName || invoice.client_name;
      const engagementIdValue = invoice.engagementId || invoice.engagement_id;
      const notesValue = invoice.notes;
      const totalAmountValue = invoice.totalAmount || invoice.total_amount || 0;
      const totalHoursValue = invoice.totalHours || invoice.total_hours || 0;

      console.log("Edit mode - Client name:", clientNameValue);
      console.log("Edit mode - Engagement ID:", engagementIdValue);
      console.log("Total amount from invoice:", totalAmountValue);
      console.log("Total hours from invoice:", totalHoursValue);

      // Use robust date extraction
      const formattedStartDate = extractDatePart(periodStartValue);
      const formattedEndDate = extractDatePart(periodEndValue);

      console.log("Formatted date values for form:", { formattedStartDate, formattedEndDate });

      // Wait a brief moment to ensure engagements are loaded and filtered
      setTimeout(() => {
        const formValues = {
          clientName: clientNameValue || "",
          engagementId: engagementIdValue?.toString() || "",
          periodStart: formattedStartDate,
          periodEnd: formattedEndDate,
          notes: notesValue || "",
          invoiceAmount: totalAmountValue
        };

        console.log("Setting form values:", formValues);

        // Reset form with invoice values
        form.reset(formValues);

        dispatch({
          type: 'SET_INVOICE_AMOUNT',
          payload: totalAmountValue
        });

        // For hourly invoices, fetch time logs to display in the modal
        const invoiceType = invoice.invoiceType || invoice.invoice_type;
        if (invoiceType === 'hourly' && engagementIdValue && formattedStartDate && formattedEndDate) {
          console.log("Fetching time logs for invoice in edit mode");
          fetchTimeLogs(engagementIdValue, formattedStartDate, formattedEndDate);
        }
      }, 100);
    } else if (!open) {
      // Reset state when modal closes
      if (state.isEditMode) {
        dispatch({ type: 'RESET' });
      }
    }
  }, [open, invoice]);

  // Update the time logs to line items conversion with proper type conversion
  const convertTimeLogsToLineItems = (logs: TimeLog[]): LineItem[] => {
    return logs.map((log) => {
      // Get hourly rate from the selected engagement since the log might not have it
      const selectedEngagement = filteredEngagements.find(e => e.id === log.engagementId);
      const hourlyRate = selectedEngagement?.hourlyRate || log.hourlyRate || '0';
      const rate = parseFloat(hourlyRate.toString());
      const amount = log.hours * rate;

      return {
        description: log.description || '',
        hours: log.hours,
        rate: rate,
        amount: amount
      };
    });
  };

  // Use the conversion function
  const lineItems = convertTimeLogsToLineItems(state.timeLogs);

  // Update the summary calculation
  const summary = useMemo(() => {
    return lineItems.reduce(
      (sum: Summary, item: LineItem) => ({
        hours: sum.hours + item.hours,
        amount: sum.amount + item.amount,
      }),
      { hours: 0, amount: 0 }
    );
  }, [lineItems]);

  // Handle invoice amount change
  const handleInvoiceAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Allow empty input
    if (value === '') {
      dispatch({
        type: 'SET_INVOICE_AMOUNT',
        payload: 0
      });
      setValue('invoiceAmount', 0);
      return;
    }

    const amount = parseFloat(value);
    if (!isNaN(amount)) {
      dispatch({
        type: 'SET_INVOICE_AMOUNT',
        payload: amount
      });
      setValue('invoiceAmount', amount);
    }
  };

  // Calculate due date using engagement's netTerms
  const calculateDueDate = (issueDate: Date, engagement: Engagement): Date => {
    const dueDate = new Date(issueDate);
    // Get net terms from engagement, default to 30 days if not available
    const netTerms = engagement.netTerms || 30;
    dueDate.setDate(dueDate.getDate() + netTerms);
    return dueDate;
  };

  // Add debug logging to onSubmit
  const onSubmit = async (data: FormValues) => {
    console.log("🚀 onSubmit called with data:", data);
    console.log("🚀 Current state:", state);
    console.log("🚀 isSubmitting:", isSubmitting);

    try {
      if (isSubmitting) {
        console.log("⚠️ Already submitting, ignoring");
        return;
      }

      setIsSubmitting(true);

      // Get the selected engagement
      const selectedEngagement = filteredEngagements.find(
        (e) => e.id.toString() === data.engagementId.toString()
      );

      if (!selectedEngagement) {
        throw new Error("Selected engagement not found");
      }

      console.log("✅ Selected engagement:", selectedEngagement);

      // Determine invoice type
      const invoiceType = selectedEngagement.engagementType || selectedEngagement.engagement_type || 'hourly';

      // Calculate amounts
      let totalAmount = 0;
      let totalHours = 0;
      let finalLineItems: LineItem[] = [];

      if (invoiceType === 'project') {
        // For project-based invoices, use the entered amount
        totalAmount = data.invoiceAmount || 0;
        totalHours = 0;
        finalLineItems = [{
          description: `${selectedEngagement.projectName} - Project Payment`,
          hours: 0,
          rate: 0,
          amount: totalAmount
        }];
      } else {
        // For hourly invoices, use the calculated summary
        totalAmount = summary.amount;
        totalHours = summary.hours;
        finalLineItems = lineItems;
      }

      console.log("💰 Calculated totals:", { totalAmount, totalHours, invoiceType });

      // Prepare submission data
      const issueDate = new Date();
      const dueDate = calculateDueDate(issueDate, selectedEngagement);

      const submissionData: InvoiceSubmission = {
        engagementId: Number(data.engagementId),
        userId: selectedEngagement.userId,
        clientName: selectedEngagement.clientName,
        projectName: selectedEngagement.projectName,
        invoiceNumber: nextInvoiceNumber,
        issueDate: toStorageDate(issueDate),
        dueDate: toStorageDate(dueDate),
        totalAmount,
        totalHours,
        lineItems: finalLineItems,
        status: 'draft',
        periodStart: data.periodStart || toStorageDate(issueDate),
        periodEnd: data.periodEnd || toStorageDate(issueDate),
        notes: data.notes || '',
        invoice_type: invoiceType,
      };

      console.log("📤 Submitting invoice data:", submissionData);

      // Submit to API
      const url = state.isEditMode && invoice
        ? `/api/invoices/${invoice.id}`
        : '/api/invoices';

      const method = state.isEditMode ? 'PUT' : 'POST';

      const response = await apiRequest(method, url, submissionData);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "Failed to save invoice");
      }

      const result = await response.json();
      console.log("✅ Invoice saved successfully:", result);

      toast({
        title: "Success",
        description: `Invoice ${state.isEditMode ? 'updated' : 'created'} successfully`,
      });

      // Close modal and refresh
      handleClose();

      // Invalidate queries to refresh lists
      queryClient.invalidateQueries({ queryKey: ["/api/invoices"] });

    } catch (error) {
      console.error("❌ Error submitting invoice:", error);
      dispatch({
        type: 'SET_ERROR',
        payload: error instanceof Error ? error.message : "An unknown error occurred"
      });
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to save invoice",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={(isOpen) => !isOpen && handleClose()} modal={false}>
      <SheetContent className="sm:max-w-[500px] w-full overflow-y-auto" pushed={open}>
        <SheetHeader>
          <SheetTitle>
            {state.isEditMode ? "Edit Invoice" : "Create New Invoice"}
          </SheetTitle>
          <SheetDescription>
            {state.isEditMode
              ? "Update the details of this invoice."
              : "Create a new invoice for your client."}
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6 mt-6">
            {/* Client Selection */}
            <FormField
              control={control}
              name="clientName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Client</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                    value={field.value}
                    disabled={state.isEditMode}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a client" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {uniqueClients.map((clientName) => (
                        <SelectItem key={clientName} value={clientName}>
                          {clientName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Engagement Selection */}
            <FormField
              control={control}
              name="engagementId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Engagement</FormLabel>
                  <Select
                    onValueChange={(value) => {
                      field.onChange(value);
                      // Reset invoice amount when engagement changes
                      setValue('invoiceAmount', 0);
                    }}
                    defaultValue={field.value?.toString()}
                    value={field.value?.toString()}
                    disabled={!state.selectedClientName || state.isEditMode}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select an engagement" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {filteredEngagements.map((engagement) => (
                        <SelectItem
                          key={engagement.id}
                          value={engagement.id.toString()}
                        >
                          {engagement.projectName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Date Range Selection */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={control}
                name="periodStart"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Period Start</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name="periodEnd"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Period End</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Conditional Content based on Engagement Type */}
            {state.invoice_type === 'project' ? (
              <div className="space-y-4 border p-4 rounded-md bg-slate-50">
                <h3 className="font-medium text-sm text-slate-900">Fixed Price Project Details</h3>

                <FormField
                  control={control}
                  name="invoiceAmount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Invoice Amount ($)</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          {...field}
                          onChange={handleInvoiceAmountChange}
                        />
                      </FormControl>
                      <p className="text-xs text-muted-foreground">
                        Total Project Value: {formatCurrency(state.invoiceTotal)}
                      </p>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            ) : (
              /* Hourly Invoice Content */
              <div className="space-y-4">
                {state.isLoadingTimeLogs ? (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                  </div>
                ) : state.timeLogs.length > 0 ? (
                  <div className="border rounded-md overflow-hidden">
                    <Table>
                      <TableBody>
                        {lineItems.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell>{item.description || 'No description'}</TableCell>
                            <TableCell className="text-right">{formatHours(item.hours)} hrs</TableCell>
                            <TableCell className="text-right">{formatCurrency(item.rate)}/hr</TableCell>
                            <TableCell className="text-right">{formatCurrency(item.amount)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow className="bg-slate-50 font-medium">
                          <TableCell>Total</TableCell>
                          <TableCell className="text-right">{formatHours(summary.hours)} hrs</TableCell>
                          <TableCell></TableCell>
                          <TableCell className="text-right">{formatCurrency(summary.amount)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                ) : (
                  watchPeriodStart && watchPeriodEnd && watchEngagementId && (
                    <div className="text-center py-8 text-muted-foreground border rounded-md border-dashed">
                      No time logs found for this period
                    </div>
                  )
                )}
              </div>
            )}

            {/* Notes Field */}
            <FormField
              control={control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Add any notes for the client..."
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Error Display */}
            {state.error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{state.error}</AlertDescription>
              </Alert>
            )}

            <SheetFooter className="gap-2 sm:justify-between">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    Saving...
                  </>
                ) : (
                  state.isEditMode ? "Update Invoice" : "Create Invoice"
                )}
              </Button>
            </SheetFooter>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
};

export default InvoiceModal;
